import React from 'react';
import {Platform, View} from 'react-native';
import {useSelector} from 'react-redux';
import {get} from 'lodash';

import Gutter from '/component/layout/Gutter';
import BaseScreen from '/component/partial/BaseScreen';
import CabBanner from '/component/partial/CabBanner';
import {PeopleToContactSection} from '/component/partial/PeopleToContactSection';
import RankProgressCard from '/component/partial/RankProgressCard';
import {ShareCartSection} from '/component/partial/ShareCartSection';
import {DashboardID} from '/constant/TestIDs';
import {SHARE_OPTAVIA_PLAN} from '/enum/popover.enum';
import {EVENT_DATA, EVENT_NAMES, SCREEN_NAMES} from '/enum/tealium.enum';
// import {userId as userIdSelector} from '/redux/selector/currentUser.selector';
// import {allCandidatesCountById} from '/redux/selector/metric.clients.selector';
import {variablesFeatureFlag} from '/redux/selector/remoteConfig.selector';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

import CandidatesSection from '../Clients/CandidatesSection';

import {Edge365Banner} from './Edge365/Edge365Banner';
import {BonusBannerCards} from './BonusBannerCards';
import {useDashboard} from './Dashboard.hook';
import {styles} from './Dashboard.style';
import {DashboardProps} from './Dashboard.types';
import {getIsCoach} from './Dashboard.util';
import {DashboardMetric} from './DashboardMetric';
import {Header} from './Header';
import {HomeMetrics} from './HomeMetrics';

import {FEATURE_FLAGS} from '/DevFeatureFlags';

const Dashboard = ({
  showChatFeature = true,
  onNavigationIconPress = () => {},
  onShowReport = () => {},
  onShowInfoPopover = () => {},
  hasHomeMetricsById = false,
  onShowRefreshInfoPopover = () => {},
  onShowMyProfile = () => {},
  currentUserCompetency = '',
  hasClientMetricsById = false,
  hasTeamsMetricsById = false,
  hasPerformanceMetrics = false,
  hasProgressionData = false,
  getClientMetrics = () => {},
  getTeamsMetrics = () => {},
  getHomeMetrics = () => {},
  getPerformanceMetrics = () => {},
  getRankProgression = () => {},
  onNavigate = () => {},
  getTeams = () => {},
  hasTeamsById = false,
  hasSameDayLastMonth = false,
  getSameDayLastMonth = () => {},
  accessibilityElementsHidden = false,
  popoverShow = () => {},
  onOpenModal = () => {},
  userId,
  lastPaidRank,
  highestRankDescription,
  coachCurrentRank,
}: DashboardProps) => {
  const {onSummaryPress} = useDashboard({
    hasHomeMetricsById,
    hasClientMetricsById,
    hasTeamsMetricsById,
    hasPerformanceMetrics,
    hasProgressionData,
    getClientMetrics,
    getTeamsMetrics,
    getHomeMetrics,
    getPerformanceMetrics,
    getRankProgression,
    onNavigate,
    getTeams,
    hasTeamsById,
    hasSameDayLastMonth,
    getSameDayLastMonth,
    userId,
    lastPaidRank,
    highestRankDescription,
    coachCurrentRank,
  });
  const isCoach = getIsCoach(currentUserCompetency);
  const featureFlag = useSelector(variablesFeatureFlag);
  // const coachUserId = useSelector(userIdSelector);
  // const allCandidatesCount = useSelector(state =>
  //   allCandidatesCountById(state, {coachId: coachUserId}),
  // );
  const {ff_edge30 = ''} = featureFlag;
  const edge30_ff = shouldReleaseFeature(ff_edge30);

  const ff_share_cart = shouldReleaseFeature(
    get(featureFlag, 'ff_share_cart', ''),
  );

  const showCandidate = shouldReleaseFeature(
    get(featureFlag, 'ff_client_referrel', ''),
  );

  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.CTA_Click,
  });
  const coachIdStr = userId ? userId.toString() : '';
  const isWeb = Platform.OS === 'web';

  // TODO
  /*  const showCandidateSection = () => {
    if (featureFlag.showCandidateManagement) {
      if (featureFlag.showAddCandidate) {
        return true;
      } else if (!featureFlag.showAddCandidate) {
        return allCandidatesCount !== 0 && !isNil(allCandidatesCount);
      }
      return true;
    }
  }; */

  return (
    <BaseScreen
      accessibilityElementsHidden={accessibilityElementsHidden}
      testID={DashboardID.dashboardScreen}
      showChatFeature={showChatFeature}
      onNavigationIconPress={onNavigationIconPress}>
      <Header
        onShowRefreshInfoPopover={onShowRefreshInfoPopover}
        onShowMyProfile={onShowMyProfile}
      />
      <Gutter
        style={{
          ...styles.section,
          ...styles.sectionTransparent,
          ...(isWeb ? styles.webSectionSpacing : {}),
        }}>
        <RankProgressCard onSummaryPress={onSummaryPress} />
      </Gutter>

      {edge30_ff && (
        <View style={isWeb ? styles.webSectionSpacing : undefined}>
          <Edge365Banner
            onNavigate={onNavigate}
            coachId={userId}
            lastPaidRank={lastPaidRank}
            highestRankDescription={highestRankDescription}
            coachCurrentRank={coachCurrentRank}
          />
        </View>
      )}
      <View style={isWeb ? styles.webSectionSpacing : undefined}>
        <DashboardMetric
          onShowReport={onShowReport}
          onShowInfoPopover={onShowInfoPopover}
          hasHomeMetricsById={hasHomeMetricsById}
          // @ts-expect-error TS(2322): Type '{ onShowReport: any; onShowInfoPopover: any;... Remove this comment to see the full error message
          hasSameDayLastMonth={hasSameDayLastMonth}
          currentUserCompetency={currentUserCompetency}
        />
      </View>
      {ff_share_cart && (
        <View style={isWeb ? styles.webSectionSpacing : undefined}>
          <ShareCartSection
            onShowInfoPopover={onShowInfoPopover}
            onCreateCart={() => {
              // @ts-ignore - onNavigate type definition seems incorrect, other components pass argument
              sendEvent({
                screenName: SCREEN_NAMES.dashboard,
                CTAtext: EVENT_DATA.CTAtextShareCart,
                CTAtype: EVENT_DATA.CTAtype,
                targetLocation: EVENT_DATA.targetLocation,
                action: 'share',
                lastPaidRank: lastPaidRank,
                coachHighestRank: highestRankDescription,
                coachCurrentRank: coachCurrentRank,
                coachID: coachIdStr,
              });

              onNavigate('card', {
                modalName: SHARE_OPTAVIA_PLAN,
                modalProps: {initialCategory: 'Create Cart'},
              });
            }}
            onEventData={{
              screenName: SCREEN_NAMES.dashboard,
              CTAtext: EVENT_DATA.CTAtextViewSavedCart,
              CTAtype: EVENT_DATA.CTAtype,
              targetLocation: EVENT_DATA.targetLocation,
              action: 'view',
              lastPaidRank: lastPaidRank,
              coachHighestRank: highestRankDescription,
              coachCurrentRank: coachCurrentRank,
              coachID: coachIdStr,
            }}
            onShareCart={() => {}}
          />
        </View>
      )}
      {showCandidate && (
        <View style={isWeb ? styles.webSectionSpacing : undefined}>
          <CandidatesSection
            onShowReport={onShowReport}
            onShowInfoPopover={onShowInfoPopover}
            onNavigate={onNavigate}
          />
        </View>
      )}
      <View style={isWeb ? styles.webSectionSpacing : undefined}>
        <PeopleToContactSection
          onShowReport={onShowReport}
          onShowInfoPopover={onShowInfoPopover}
          onOpenModal={onOpenModal}
          parentScreenName={SCREEN_NAMES.dashboard}
        />
      </View>
      <View style={isWeb ? styles.webSectionSpacing : undefined}>
        <BonusBannerCards
          popoverShow={popoverShow}
          parentScreenName={SCREEN_NAMES.dashboard}
        />
      </View>
      {FEATURE_FLAGS.showCabBonus && isCoach && (
        <View style={isWeb ? styles.webSectionSpacing : undefined}>
          <CabBanner />
        </View>
      )}
      <View style={isWeb ? styles.webSectionSpacing : undefined}>
        <HomeMetrics
          onShowReport={onShowReport}
          onShowInfoPopover={onShowInfoPopover}
          hasHomeMetricsById={hasHomeMetricsById}
          currentUserCompetency={currentUserCompetency}
        />
      </View>
    </BaseScreen>
  );
};

export default Dashboard;
